import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Loader2, X, Trash2, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const SERVICES = [["sale", "Sale"], ["purchase", "Purchase"], ["tenancy", "Tenancy"], ["maintenance", "Maintenance"]];
const CITIES = ["Mumbai", "Pune", "Kolhapur"];
const TINT = { sale: "border-emerald-200", purchase: "border-amber-200", tenancy: "border-rose-200", maintenance: "border-sky-200" };
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const BTN = "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all shadow-sm active:scale-[0.98]";

export default function ProductSetup() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/products"); setProducts(data); }
    catch (e) { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/admin/products/${id}`); setProducts((p) => p.filter((x) => x.id !== id)); toast.success("Product removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <div className="max-w-4xl" data-testid="product-setup-page">
      <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Admin</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2"><Package className="h-7 w-7 text-emerald-600" /> Product Setup</h1>
      <p className="mt-2 text-sm text-slate-500">Add products under each service. Clients can then select them for their properties.</p>
      <ServiceAvailabilityPanel />
      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : SERVICES.map(([key, label]) => (
        <section key={key} className="mt-8" data-testid={`service-section-${key}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">{label}</h2>
            <button onClick={() => setAdding(key)} data-testid={`add-product-${key}`} className={`${BTN} px-4 py-2 text-sm`}><Plus className="h-4 w-4" /> Add product</button>
          </div>
          <div className="mt-3 space-y-2">
            {products.filter((p) => p.service === key).length === 0 && (
              <div className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-xl p-4 text-center">No products yet.</div>
            )}
            {products.filter((p) => p.service === key).map((p) => (
              <div key={p.id} data-testid="product-row" className={`flex items-center gap-4 bg-white border ${TINT[key]} rounded-xl px-4 py-3 shadow-sm`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.details}</div>
                </div>
                <div className="text-sm font-mono text-slate-800 shrink-0">{p.price_type === "fixed" ? inr(p.price) : `${p.percentage}% of deal`}</div>
                <button onClick={() => remove(p.id)} data-testid="delete-product-button" className="text-slate-400 hover:text-rose-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </section>
      ))}
      {adding && <AddProductModal service={adding} onClose={() => setAdding(null)} onAdded={(p) => { setProducts((x) => [...x, p]); setAdding(null); }} />}
    </div>
  );
}

function ServiceAvailabilityPanel() {
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/service-availability").then((r) => setAvail(r.data.availability)).catch(() => toast.error("Failed to load availability")); }, []);
  const toggle = (city, svc) => setAvail((a) => ({ ...a, [city]: { ...a[city], [svc]: !a[city]?.[svc] } }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/service-availability", { availability: avail }); setAvail(data.availability); toast.success("Availability saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };
  if (!avail) return null;
  return (
    <section className="mt-8 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="service-availability-panel">
      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-5 w-5 text-emerald-600" /> Service availability by city</h2>
      <p className="mt-1 text-sm text-slate-500">Enable the services your firm offers in each city. Disabled services are hidden from clients for properties in that city.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="py-2 pr-4 font-medium">City</th>
              {SERVICES.map(([k, label]) => <th key={k} className="py-2 px-3 font-medium text-center">{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {CITIES.map((city) => (
              <tr key={city} className="border-t border-slate-100" data-testid={`availability-row-${city}`}>
                <td className="py-2.5 pr-4 font-medium text-slate-800">{city}</td>
                {SERVICES.map(([k]) => (
                  <td key={k} className="py-2.5 px-3 text-center">
                    <input type="checkbox" data-testid={`availability-${city}-${k}`} checked={!!avail[city]?.[k]} onChange={() => toggle(city, k)} className="h-4 w-4 accent-emerald-600 cursor-pointer" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={save} disabled={busy} data-testid="save-availability-button" className={`${BTN} mt-4 px-5 py-2.5 disabled:opacity-60`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save availability
      </button>
    </section>
  );
}

function AddProductModal({ service, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", details: "", price_type: "fixed", price: "", percentage: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { service, name: form.name, details: form.details, price_type: form.price_type,
        price: form.price_type === "fixed" ? Number(form.price) : null,
        percentage: form.price_type === "variable" ? Number(form.percentage) : null };
      const { data } = await api.post("/admin/products", payload);
      toast.success("Product added");
      onAdded(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setBusy(false); }
  };
  const field = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="add-product-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-900 capitalize">Add {service} product</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm text-slate-600">Product Name</label>
            <input required data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
          </div>
          <div>
            <label className="text-sm text-slate-600">Product Details</label>
            <textarea required data-testid="product-details-input" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={2} className={field} />
          </div>
          <div>
            <label className="text-sm text-slate-600">Price type</label>
            <div className="mt-2 flex gap-4">
              {["fixed", "variable"].map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm text-slate-700 capitalize cursor-pointer">
                  <input type="radio" name="price_type" data-testid={`price-type-${t}`} checked={form.price_type === t} onChange={() => setForm({ ...form, price_type: t })} /> {t}
                </label>
              ))}
            </div>
          </div>
          {form.price_type === "fixed" ? (
            <div>
              <label className="text-sm text-slate-600">Price (₹)</label>
              <input type="number" required data-testid="product-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} />
            </div>
          ) : (
            <div>
              <label className="text-sm text-slate-600">Percentage of deal value (%)</label>
              <input type="number" step="0.01" required data-testid="product-percentage-input" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} className={field} />
            </div>
          )}
          <button type="submit" disabled={busy} data-testid="save-product-button" className={`${BTN} w-full justify-center px-6 py-3 disabled:opacity-60`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save Product
          </button>
        </form>
      </div>
    </div>
  );
}
